import Compressor from "compressorjs";

const LOSSY_COMPRESSIBLE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/bmp",
  "image/gif",
  "image/heic",
  "image/heif",
]);
const DEFAULT_COMPRESS_FORMAT = "image/jpeg";
const MAX_COMPRESS_ATTEMPTS = 4;

export interface CompressImageOptions {
  maxSizeBytes: number;
  maxWidthOrHeight?: number;
  outputType?: string;
  /** 即使文件未超过 maxSizeBytes，也强制走压缩并输出为 outputType（用于格式归一化） */
  forceConvert?: boolean;
}

const replaceFileExtension = (fileName: string, fileType: string): string => {
  const ext = fileType.split("/")[1] ?? "jpg";
  return fileName.replace(/\.[^.]+$/, "") + `.${ext}`;
};

export const isLossyCompressibleImage = (file: File): boolean =>
  LOSSY_COMPRESSIBLE_IMAGE_TYPES.has(file.type.toLowerCase());

const runCompressor = async (
  file: File,
  quality: number,
  options: Pick<CompressImageOptions, "maxWidthOrHeight" | "outputType">,
): Promise<File> => {
  const result = await new Promise<File | Blob>((resolve, reject) => {
    const compressor = new Compressor(file, {
      quality,
      mimeType: options.outputType ?? DEFAULT_COMPRESS_FORMAT,
      maxWidth: options.maxWidthOrHeight,
      maxHeight: options.maxWidthOrHeight,
      convertSize: 0,
      checkOrientation: true,
      success: resolve,
      error: reject,
    });
    void compressor;
  });

  if (result instanceof File) {
    return result;
  }

  const fileType = options.outputType ?? DEFAULT_COMPRESS_FORMAT;
  return new File([result], replaceFileExtension(file.name, fileType), {
    type: fileType,
    lastModified: Date.now(),
  });
};

export const compressImageToLimit = async (file: File, options: CompressImageOptions): Promise<File> => {
  const outputType = (options.outputType ?? DEFAULT_COMPRESS_FORMAT).toLowerCase();
  const sizeOverLimit = file.size > options.maxSizeBytes;
  const needsTypeConversion = !!options.forceConvert && file.type.toLowerCase() !== outputType;

  // 既不超限也不需要转换格式，直接返回原文件
  if (!sizeOverLimit && !needsTypeConversion) return file;

  const stillNeedsWork = (current: File): boolean => {
    if (current.size > options.maxSizeBytes) return true;
    if (options.forceConvert && current.type.toLowerCase() !== outputType) return true;
    return false;
  };

  let compressedFile = file;
  let attempt = 0;

  // 至少跑一次压缩（即使大小达标，强制格式转换也需要走 Compressor 解码再编码）
  while ((attempt === 0 || stillNeedsWork(compressedFile)) && attempt < MAX_COMPRESS_ATTEMPTS) {
    const quality = Math.max(0.4, 0.9 - attempt * 0.15);
    const widthRatio = Math.max(0.4, 1 - attempt * 0.2);
    const maxWidthOrHeight = options.maxWidthOrHeight ? Math.round(options.maxWidthOrHeight * widthRatio) : undefined;
    compressedFile = await runCompressor(compressedFile, quality, {
      maxWidthOrHeight,
      outputType: options.outputType,
    });

    attempt += 1;
  }

  if (compressedFile.type.toLowerCase() !== outputType) {
    return new File([compressedFile], replaceFileExtension(file.name, outputType), {
      type: outputType,
      lastModified: Date.now(),
    });
  }

  return compressedFile;
};
